import { isManagerVar, usernameVar } from '@graphql/cache';
import useLogin from '@graphql/hooks/useLogin';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, Icon, TextField } from '@mayoral/ui';
import {
  Alert,
  AlertTitle,
  Box,
  FormControlLabel,
  IconButton,
  Snackbar,
  Stack,
} from '@mui/material';
import { LoginMutationVariables } from '@sdk/core';
import { writeToCookies } from '@utils/cookies';
import { useTranslation } from 'next-i18next';
import React, { FC, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { loginSchema } from './LoginForm.schema';

import { AuthCookieExpiration, StorageTokens } from '@/lib/constants';

interface LoginFormProps {
  manager: boolean;
}

const LoginForm: FC<LoginFormProps> = ({ manager = false }) => {
  const { t } = useTranslation();
  const [hasErrors, setHasErrors] = useState(false);
  const schema = loginSchema(manager, t);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LoginMutationVariables>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: schema.getDefaultFromShape() as LoginMutationVariables,
    resolver: yupResolver(schema),
  });
  const [login, { loading, error }] = useLogin({
    onError: (e) => {
      setHasErrors(true);
      // rethrow error as-is
      throw e;
    },
  });

  const onSubmit: SubmitHandler<LoginMutationVariables> = async (data) => {
    // Saves the username from the last login attempt and updates the reactive var
    // FIXME: This should be provided by the REST service.
    writeToCookies(StorageTokens.USERNAME, data.username, {
      expires: AuthCookieExpiration,
    });
    writeToCookies(StorageTokens.IS_MANAGER, manager, {
      expires: AuthCookieExpiration,
    });
    writeToCookies(StorageTokens.SELECTED_WORKSHOP, +data.username);
    usernameVar(data.username);
    isManagerVar(manager);

    login({ variables: { ...data } }).catch(() => {});
  };

  return (
    <Stack direction="column">
      {error && (
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={hasErrors}>
          <Alert
            severity="error"
            sx={{ '& .MuiAlert-message': { maxWidth: 285, wordBreak: 'break-word' } }}
            title={error.message}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setHasErrors(false)}
              >
                <Icon name="CloseS" />
              </IconButton>
            }
          >
            <AlertTitle>Error</AlertTitle> {error.message}
          </Alert>
        </Snackbar>
      )}

      <Box
        component="form"
        id="login-form"
        sx={{ display: 'flex', flexDirection: 'column' }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box sx={{ typography: 'h3', fontWeight: 500, mb: 0.75 }}>{t('INICIAR_SESION')}</Box>
        <Box sx={{ typography: 'body1', color: 'grey.700', mb: 3 }}>{t('CREDENCIALES_ACCESO')}</Box>

        <Controller
          control={control}
          name="username"
          render={({ field: { onChange, value } }) => (
            <TextField
              onChange={onChange}
              value={value}
              error={hasErrors || !!errors.username}
              required
              name="username"
              label={t('USUARIO')}
              autoComplete="username"
              id="username"
              sx={{ mb: 1.5 }}
              variant="outlined"
              helperText={
                errors.username && (
                  <Stack component="span" direction="row" alignItems="flex-start">
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 0,
                        position: 'relative',
                        top: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 14,
                        height: 14,
                        marginRight: 0.5,
                        backgroundColor: 'error.main',
                        borderRadius: 50,
                      }}
                    >
                      <Icon
                        name="CloseS"
                        sx={{ fontSize: 10, path: { fill: (theme) => theme.palette.common.white } }}
                      />
                    </Box>
                    {errors.username.message}
                  </Stack>
                )
              }
              disabled={loading}
            />
          )}
        />

        <Controller
          control={control}
          name="password"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <TextField
              onChange={onChange}
              value={value}
              error={hasErrors}
              type="password"
              required
              name="password"
              label={t('CONTRASEÑA')}
              autoComplete="current-password"
              id="password"
              variant="outlined"
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />

        <FormControlLabel
          control={<Checkbox disabled={loading} />}
          label="Recordar datos"
          sx={{ mt: 0.5 }}
        />

        <Button
          type="submit"
          form="login-form"
          variant="contained"
          color="primary"
          size="large"
          loading={loading}
          endIcon={<Icon name="ArrowRightS" />}
          sx={{ mt: 1.5 }}
        >
          {t('CONTINUAR')}
        </Button>
      </Box>
    </Stack>
  );
};

export default LoginForm;
